import { useState } from "react";

// Shared layout constants for any "desktop" window host (ProductHub, SettingsHub, ...).
// Exported so callers can build custom `sizing` functions (see openOrUpdateWindow) without
// re-declaring these magic numbers.
export const WINDOW_START_X = 140;
export const WINDOW_START_Y = 90;
export const WINDOW_CASCADE_OFFSET = 32;
export const DEFAULT_WINDOW_WIDTH = 640;
export const DEFAULT_WINDOW_HEIGHT = 480;
export const MIN_WINDOW_WIDTH = 360;
export const MIN_WINDOW_HEIGHT = 280;
// "Maximized" windows leave a visible margin of desktop showing on every side, so they
// still read as a floating window rather than taking over the whole screen.
export const MAXIMIZE_TOP = 160;
export const MAXIMIZE_MARGIN = 110;

function cascadeLayout(prevOpenWindows, icon) {
  return {
    x: WINDOW_START_X + prevOpenWindows.length * WINDOW_CASCADE_OFFSET,
    y: WINDOW_START_Y + prevOpenWindows.length * WINDOW_CASCADE_OFFSET,
    width: Math.min(icon?.width ?? DEFAULT_WINDOW_WIDTH, window.innerWidth - 32),
    height: Math.min(icon?.height ?? DEFAULT_WINDOW_HEIGHT, window.innerHeight - 96),
  };
}

function maximizeLayout() {
  return {
    x: MAXIMIZE_MARGIN,
    y: MAXIMIZE_TOP,
    width: Math.max(MIN_WINDOW_WIDTH, window.innerWidth - MAXIMIZE_MARGIN * 2),
    height: Math.max(MIN_WINDOW_HEIGHT, window.innerHeight - MAXIMIZE_TOP - MAXIMIZE_MARGIN),
  };
}

// Unlike maximizeLayout, this leaves no margin at all (including over the left sidebar) —
// only `minY` (the sticky header's height) is respected, since that's the one thing a
// window truly can't cover. For icons that opt in via `icon.fill` rather than `icon.maximize`
// (currently just My Runbooks, which explicitly wants to cover the sidebar).
function fillLayout(minY) {
  return {
    x: 0,
    y: minY,
    width: window.innerWidth,
    height: window.innerHeight - minY,
  };
}

// Manages the open/close/focus/resize/drag state for a set of floating DesktopWindow
// instances. Icon-position persistence for the desktop icons themselves is a separate
// concern — see useDesktopIconPositions.
export function useDesktopWindows({ minY = 65 } = {}) {
  const [openWindows, setOpenWindows] = useState([]);

  const focusWindow = (id) => {
    setOpenWindows((prev) => {
      const maxZ = prev.length ? Math.max(...prev.map((w) => w.zIndex)) : 0;
      return prev.map((w) => (w.id === id ? { ...w, zIndex: maxZ + 1 } : w));
    });
  };

  const openWindow = (icon) => {
    setOpenWindows((prev) => {
      if (prev.some((w) => w.id === icon.id)) {
        const maxZ = Math.max(...prev.map((w) => w.zIndex));
        // Re-opening (e.g. double-clicking its desktop icon) must restore a minimized
        // window, not just bring its collapsed titlebar to the front.
        return prev.map((w) => (w.id === icon.id ? { ...w, zIndex: maxZ + 1, isMinimized: false } : w));
      }
      const maxZ = prev.length ? Math.max(...prev.map((w) => w.zIndex)) : 0;
      const layout = icon.fill ? fillLayout(minY) : icon.maximize ? maximizeLayout() : cascadeLayout(prev, icon);
      return [
        ...prev,
        {
          id: icon.id,
          title: icon.label,
          Icon: icon.Icon,
          color: icon.color,
          to: icon.to,
          ...layout,
          zIndex: maxZ + 1,
          isMinimized: false,
          isMaximized: false,
          prevLayout: null,
        },
      ];
    });
  };

  // Generalizes the "singleton form window" pattern: if a window with `fixedId` is
  // already open, patch its title/meta and bring it to front instead of stacking a
  // second window (e.g. clicking "Edit" on a different row while the form is already
  // open swaps its content). `sizing` is optional: omit for the normal cascade layout,
  // or pass an explicit {x,y,width,height} or a (prevOpenWindows) => {...} function for
  // a custom layout (wider window, offset position, etc). `to`, if given, is the virtual
  // route this window's content lives at — DesktopWindow only renders a pop-out button
  // when a window has one, so pass it for anything meant to be pop-out-able.
  const openOrUpdateWindow = (fixedId, { title, Icon, color, meta, sizing, to }) => {
    setOpenWindows((prev) => {
      const maxZ = prev.length ? Math.max(...prev.map((w) => w.zIndex)) : 0;
      if (prev.some((w) => w.id === fixedId)) {
        // Same reasoning as openWindow: re-triggering this (e.g. "Edit" on another row)
        // must restore the window if it was minimized, not just re-focus it collapsed.
        // `to` is refreshed too — reopening with a different runbookId/executionId etc.
        // means the pop-out target has to follow, not stay pointed at the old one.
        return prev.map((w) => (w.id === fixedId ? { ...w, title, zIndex: maxZ + 1, meta, to, isMinimized: false } : w));
      }
      const layout = typeof sizing === "function" ? sizing(prev) : sizing ?? cascadeLayout(prev);
      return [
        ...prev,
        {
          id: fixedId,
          title,
          Icon,
          color,
          to,
          ...layout,
          zIndex: maxZ + 1,
          meta,
          isMinimized: false,
          isMaximized: false,
          prevLayout: null,
        },
      ];
    });
  };

  const closeWindow = (id) => {
    setOpenWindows((prev) => prev.filter((w) => w.id !== id));
  };

  const minimizeWindow = (id) => {
    setOpenWindows((prev) => prev.map((w) => (w.id === id ? { ...w, isMinimized: !w.isMinimized } : w)));
  };

  // Toggles between the window's current (dragged/resized) layout and a true edge-to-edge
  // fill, restoring the prior layout — not just the last-known size — on the way back. This
  // is deliberately always `fillLayout`, regardless of how the window was first opened
  // (plain cascade, margined `icon.maximize`, or edge-to-edge `icon.fill`) — the maximize
  // *button* means "take all available space," same as any OS window manager, even when a
  // window's nice landing size on open was smaller/margined.
  // Also clears isMinimized: maximize/restore both mean "show me the window", so a
  // minimized window must not stay collapsed just because only isMaximized flipped.
  const toggleMaximizeWindow = (id) => {
    setOpenWindows((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        if (w.isMaximized) {
          const restored = w.prevLayout ?? cascadeLayout(prev.filter((x) => x.id !== id));
          return { ...w, ...restored, isMaximized: false, isMinimized: false, prevLayout: null };
        }
        return {
          ...w,
          ...fillLayout(minY),
          isMaximized: true,
          isMinimized: false,
          prevLayout: { x: w.x, y: w.y, width: w.width, height: w.height },
        };
      })
    );
  };

  const handleWindowResize = (id, { width, height }) => {
    setOpenWindows((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        const maxWidth = window.innerWidth - w.x - 16;
        const maxHeight = window.innerHeight - w.y - 16;
        return {
          ...w,
          width: Math.min(Math.max(width, MIN_WINDOW_WIDTH), Math.max(maxWidth, MIN_WINDOW_WIDTH)),
          height: Math.min(Math.max(height, MIN_WINDOW_HEIGHT), Math.max(maxHeight, MIN_WINDOW_HEIGHT)),
        };
      })
    );
  };

  const handleWindowDragEnd = ({ active, delta }) => {
    if (!delta.x && !delta.y) return;
    const id = String(active.id).replace(/^window-/, "");
    setOpenWindows((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        const maxX = window.innerWidth - 240;
        const maxY = window.innerHeight - 48;
        return {
          ...w,
          x: Math.min(Math.max(w.x + delta.x, 0), maxX),
          y: Math.min(Math.max(w.y + delta.y, minY), maxY),
        };
      })
    );
  };

  return {
    openWindows,
    openWindow,
    openOrUpdateWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    toggleMaximizeWindow,
    handleWindowResize,
    handleWindowDragEnd,
  };
}
